// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import * as SS from 'styled-system';
import AmpContext from 'utils/AmpContext';
import { textStyles as styles, getClassName } from '../theme';

const styledSystem = css`
  ${SS.space}
  ${SS.overflow}
  ${SS.color}
  ${SS.opacity}
  ${SS.fontFamily}
  ${SS.fontSize}
  ${SS.fontWeight}
  ${SS.fontStyle}
  ${SS.letterSpacing}
  ${SS.textAlign}
  ${SS.lineHeight}
  ${SS.borders}
  ${SS.borderColor}
  ${SS.display}
  ${SS.verticalAlign}
`;

const Text = styled(
  ({ tag = 'div', styleAs, ...props }: { tag?: string, styleAs?: string }) => {
    const otherProps = Object.keys(props).reduce(
      (acc, key) =>
        [
          'm',
          'mt',
          'mb',
          'ml',
          'mr',
          'mx',
          'my',
          'p',
          'pt',
          'pb',
          'pl',
          'pr',
          'px',
          'py',
          'overflow',
          'color',
          'fontFamily',
          'fontSize',
          'fontWeight',
          'letterSpacing',
          'textAlign',
          'textTransform',
          'textOverflow',
          'textDecoration',
          'whiteSpace',
          'lineHeight',
          'border',
          'borderLeft',
          'borderRight',
          'borderTop',
          'borderBottom',
          'borderColor',
          'opacity',
          'display',
          'verticalAlign',
          'textShadow',
          'styleAs'
        ].includes(key)
          ? acc
          : { ...acc, [key]: props[key] },
      {}
    );
    const Tag = tag;
    return <Tag {...otherProps} />;
  }
).attrs(
  ({
    textOverflow,
    textTransform,
    textDecoration,
    whiteSpace,
    textShadow
  }) => ({
    style: {
      textOverflow,
      textTransform,
      textDecoration,
      whiteSpace,
      textShadow
    }
  })
)`
  ${props =>
    props.styleAs &&
    styles[props.styleAs] &&
    `
    ${styles[props.styleAs]}
  `}
  ${props =>
    props.$lineClamp &&
    `
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-line-clamp: ${props.$lineClamp};
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;  
    overflow: hidden;
  `}
  ${styledSystem}
`;

export default Text;

const StyledRichText = styled(
  ({
    tag = 'div',
    simple,
    small,
    normalize,
    html,
    ampEnabled,
    className,
    ...props
  }: {
    tag?: string,
    simple?: boolean,
    small?: boolean,
    normalize?: boolean,
    html?: string,
    ampEnabled?: boolean,
    className: string
  }) => {
    const cleanHtml = html
      ? html.replace(
          /<(p|h[1-6]|blockquote)>(<br\/?>)?<\/(p|h[1-6]|blockquote)>/g,
          ''
        )
      : '';
    return (
      <Text
        tag={tag}
        dangerouslySetInnerHTML={html ? { __html: cleanHtml } : undefined}
        className={getClassName(className, ['rich-text'])}
        {...props}
      />
    );
  }
)`
  &.idlst-rchtxt {
    ${props =>
      !props.ampEnabled &&
      `
  ${
    props.simple && !props.ampEnabled
      ? `
    h1, h2, h3, h4, h5 {
      margin: 1em 0;
      opacity: 0.8;
      font-size: inherit !important;
    }
  `
      : ''
  }
  ${props.small &&
    `
    ${styles.bodySmall}

    h1, h2, h3, h4 {
      margin: 1em 0;
      ${styles.h4}
    }
  `}
  ${
    props.normalize && !props.ampEnabled
      ? `
    h1, h2, h3 {
      ${styles.h3}
    }
    
    h4, h5 {
      font-size: inherit !important;
    }

    h1, h2, h3, h4, h5 {
      margin: 1em 0;
      opacity: 0.8;
    }
  `
      : ''
  }`}
  }
`;

export const RichText = (props: Object) => (
  <AmpContext.Consumer>
    {ampEnabled => <StyledRichText ampEnabled={ampEnabled} {...props} />}
  </AmpContext.Consumer>
);

export const H1 = ({ tag = 'h1', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="h1" {...props} />
);

export const H2 = ({ tag = 'h2', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="h2" {...props} />
);

export const H3 = ({ tag = 'h3', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="h3" {...props} />
);

export const H4 = ({ tag = 'h4', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="h4" {...props} />
);

export const H5 = ({ tag = 'h5', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="h5" {...props} />
);

export const H6 = ({ tag = 'h6', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="h6" {...props} />
);

export const HeadingSmall = ({ tag = 'h5', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="headingSmall" {...props} />
);

export const BodyLarge = ({ tag = 'div', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="bodyLarge" {...props} />
);

export const Body = ({ tag = 'div', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="body" {...props} />
);

export const BodySmall = ({ tag = 'div', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="bodySmall" {...props} />
);

export const BodyMicro = ({ tag = 'div', ...props }: { tag?: string }) => (
  <Text tag={tag} styleAs="bodyMicro" {...props} />
);

export const Underline = styled(
  ({ tag = 'span', ...props }: { tag?: string }) => (
    <Text tag={tag} styleAs="underline" {...props} />
  )
)`
  @keyframes aUnderline {
    from {
      background-size: 0 0.3em;
    }
    to {
      background-size: 100% 0.3em;
    }
  }
  background-repeat: no-repeat;
  background-size: 0 0.3em;
  animation: aUnderline 1s cubic-bezier(0.215, 0.61, 0.355, 1) 1s forwards;
`;
